import { useMemo, useState } from "react";
import { UploadImageField } from "../../../../common/components/form/UploadImageField/UploadImageField";
import { LEAD_STATUS } from "../../../../types/lead.type";
import { UploadDocumentField } from "../../../renters/components/forms/fields/UploadDocumentField";
import {
  startConversation,
  updateLeadProps,
} from "../../../../common/services/leads.api";
import { GenericInfoField } from "../../../../common/helpers/generic-info-field";
import { personalLeadInfo } from "./lead-edit-constants";
import { FaCheckCircle, FaSpinner, FaTimesCircle } from "react-icons/fa";
import { useLeadDashboardContext } from "../lead-dashboard/useLeadDashboardContext";
import { Button } from "@/components/ui/button";
import { ModalViewLogs } from "@/modules/logsDetails/logsDetails.modal";
import { maskPhone } from "@/common/helpers/mask.helpers";

const STATUS_LABELS = {
  [LEAD_STATUS.NewLead]: "Novo Lead",
  [LEAD_STATUS.Incomplete]: "Em negociação",
  [LEAD_STATUS.ReadyToRent]: "Validado",
  [LEAD_STATUS.Converted]: "Convertido",
  [LEAD_STATUS.Archived]: "Arquivado",
  [LEAD_STATUS.Sdr]: "SDR",
  [LEAD_STATUS.UnderNegotiation]: "Em Tratativa",
};

const STATUS_COLORS = {
  [LEAD_STATUS.NewLead]: "bg-bluePrimary text-white",
  [LEAD_STATUS.Incomplete]: "bg-yellowPrimary",
  [LEAD_STATUS.ReadyToRent]: "bg-greenPrimary text-white",
  [LEAD_STATUS.Converted]: "bg-yellowPrimary",
};

function getStatusLabels(status: LEAD_STATUS) {
  return (
    <span className={`px-2 p-1 rounded-md ${STATUS_COLORS[status]}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export function LeadInfo() {
  const [isLoading, setIsLoading] = useState(false);
  const { lead } = useLeadDashboardContext();
  const leadId = lead._id;
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);

  const pathToUpload = useMemo(() => `lead/${leadId}`, [leadId]);

  const saveLeadAvatarImage = async (avatar) => {
    await updateLeadProps(leadId || "", {
      avatar,
    });
  };

  const handleStartConversation = async (leadId: string) => {
    setIsLoading(true);
    const result = await startConversation(leadId);
    if (result.ok) {
      setIsLoading(false);
      window.open(result.data, "_blank");
    }
  };

  const setLeadInfo = personalLeadInfo(lead);

  return (
    <>
      <div className="tabs-border-line">
        <section className="tabs-item-style">
          <div className="max-w-7xl grid grid-rows-3 gap-3 grid-cols-3 rounded-md dark:text-black bg-[#f2f2f2]">
            <div className="row-span-3">
              <UploadDocumentField
                currentSrc={lead.avatar}
                pathToUpload={pathToUpload}
                uploadName="avatar-lead"
                handleUploadSrc={(src) => saveLeadAvatarImage(src)}
              >
                {({ src, onNewFile, onRemoveClick }) => (
                  <UploadImageField
                    src={src}
                    onNewFile={onNewFile}
                    onRemoveClick={onRemoveClick}
                  />
                )}
              </UploadDocumentField>
            </div>
            <div className="row-span-3 col-span-2 grid grid-rows-3 grid-cols-2">
              <h4 className="font-semibold text-2xl col-span-2">
                {" "}
                {lead.name}{" "}
              </h4>

              <span className="p-2 flex items-center bg-white/30 rounded-l-md border border-gray/50 border-r-0">
                {" "}
                Whatsapp:{" "}
              </span>
              <span className="p-2 flex items-center gap-2 bg-white/30 rounded-r-md border border-gray/50 border-l-0">
                <a
                  onClick={() =>
                    handleStartConversation(lead._id?.toString() || "")
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-bluePrimary underline cursor-pointer"
                >
                  {maskPhone(lead.whatsapp)}
                </a>
                {isLoading && (
                  <FaSpinner className="animate-spin text-gray-500" size={16} />
                )}
              </span>
              <span className="p-2 flex items-center bg-white/50 rounded-l-md border border-gray/50 border-r-0">
                {" "}
                Status:{" "}
              </span>
              <span className="p-2 flex items-center bg-white/50 rounded-r-md border border-gray/50 border-l-0">
                {getStatusLabels(lead.status)}
              </span>
            </div>
          </div>
        </section>

        <div className="div-tabs-border-line"></div>

        <section className="tabs-item-style">
          <h2 className="text-2xl font-semibold mb-4">Informações Pessoais</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 grid-rows-3 mb-8 gap-2">
            {setLeadInfo.map((info) => {
              return (
                <GenericInfoField
                  id={info.id}
                  spanSize={info.spanSize}
                  label={info.label}
                  value={info.value}
                />
              );
            })}
          </div>
          <div className="flex items-center mb-6">
            {
              <span className="text-md font-semibold text-gray-600">
                Validação Jurídica:
              </span>
            }
            <span className="ml-2">
              {lead.jurValidation.validated === false ? (
                <></>
              ) : lead.jurValidation.status ? (
                <FaCheckCircle size={24} className="text-greenPrimary" />
              ) : (
                <FaTimesCircle size={24} className="text-redPrimary" />
              )}
            </span>
          </div>

          {/* <section>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="relative h-20 w-full sm:w-full color flex flex-col space-y-4">
              <p className="text-md font-semibold text-gray-600">Foi indicado por alguém?</p>
              <div className="flex items-center space-x-4">
                <label htmlFor="cpfDoIndicadorOptions.indicated" className="flex items-center space-x-2 text-sm font-medium text-gray-600 cursor-pointer">
                  <input
                    id="cpfDoIndicadorOptions.indicated"
                    name="cpfDoIndicadorOptions.indicated"
                    type="checkbox"
                    checked={lead.cpfDoIndicadorOptions?.indicated}
                    className="hidden"
                  />
                  {lead.cpfDoIndicadorOptions?.indicated ? (
                    <ImCheckboxChecked className="text-greenPrimary" />
                  ) : (
                    <ImCheckboxUnchecked className="text-redPrimary" />
                  )}
                  <span>Sim</span>
                </label>
                <label htmlFor="cpfDoIndicadorOptions.notIndicated" className="flex items-center space-x-2 text-sm font-medium text-gray-600 cursor-pointer">
                  <input
                    id="cpfDoIndicadorOptions.notIndicated"
                    name="cpfDoIndicadorOptions.notIndicated"
                    type="checkbox"
                    checked={lead.cpfDoIndicadorOptions?.notIndicated}
                    className="hidden"
                  />
                  {lead.cpfDoIndicadorOptions?.notIndicated ? (
                    <ImCheckboxChecked className="text-greenPrimary" />
                  ) : (
                    <ImCheckboxUnchecked className="text-redPrimary" />
                  )}
                  <span>Não</span>
                </label>
              </div>
            </div>
            <div></div>
            <div>
              {lead.cpfDoIndicadorOptions?.indicated ? (
                <>
                  <label className="font-semibold text-sm">CPF da pessoa que o(a) indicou:</label>
                  <p className="border border-gray h-[43px] text-gray2 mb-3 p-2 overflow-hidden rounded w-full bg-[#ffffff]">{lead.cpfDoIndicador}</p>
                </>
              ) : null}
            </div>
          </div>
        </section> */}
        </section>
        <div className="div-tabs-border-line"></div>
        <section className="tabs-item-style">
          <div className="max-w-7xl mx-auto ">
            <Button
              variant="destructive"
              className={`bg-redPrimary text-white font-semibold p-2 rounded-md mb-2 sm:mb-0`}
              onClick={() => setIsLogModalOpen(true)}
            >
              Visualizar Logs
            </Button>
          </div>
        </section>
      </div>
      {isLogModalOpen && leadId && (
        <ModalViewLogs
          onClose={() => setIsLogModalOpen(false)}
          entity="lead" // Entidade fixa como solicitado
          entityId={leadId} // ID do veículo do contexto
        />
      )}
    </>
  );
}
